/**
 * Mobile card representation of a single rule.
 *
 * Replaces the desktop RulesTable row on phones. Stacks the rule's
 * information vertically for thumb reach and small screens.
 * Shares the same data (Rule) and the same mutation callbacks as
 * RuleRow, so the parent passes identical props and switches on
 * useBreakpoint.
 *
 * Card layout, top to bottom:
 *   - Priority label (muted, small-caps)
 *   - Rule name + Status toggle (in head row)
 *   - Applied-to chip
 *   - If section (conditions)
 *   - Then section (settings)
 *   - Footer: "Applied N times · <relative>" and three-dot menu
 *
 * Mobile cards do not expose the per-row checkbox or Auto-post
 * toggle in PR 9 to keep the card compact and tappable. Both are
 * accessible via the three-dot menu (future rounds may add bulk
 * actions here if the pattern warrants it).
 */

import { useRef, type CSSProperties } from "react";
import type { Rule } from "../rulesTypes";
import {
  describeCondition,
  describeSettings,
  formatLastApplied,
  getAppliedToDotColor,
  getAppliedToLabel,
} from "../rulesUtils";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  bgFade: "#F3F5F5",
};

interface RuleCardProps {
  rule: Rule;
  onToggleStatus: (next: boolean) => void;
  onOpenMenu: (anchorEl: HTMLElement) => void;
}

export function RuleCard({ rule, onToggleStatus, onOpenMenu }: RuleCardProps) {
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const paused = rule.status === "paused";

  const cardStyle: CSSProperties = {
    background: "#ffffff",
    border: `1px solid ${PALETTE.line}`,
    borderRadius: 10,
    padding: 14,
    margin: "10px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    opacity: paused ? 0.6 : 1,
  };

  const handleMenuClick = () => {
    if (menuBtnRef.current) onOpenMenu(menuBtnRef.current);
  };

  const footerText = paused
    ? `Paused · applied ${rule.matchCount} times total`
    : `Applied ${rule.matchCount} ${rule.matchCount === 1 ? "time" : "times"} · ${formatLastApplied(rule.lastAppliedAt)}`;

  return (
    <div style={cardStyle}>
      <div style={priorityLabelStyle}>Priority {rule.priority}</div>

      <div style={headStyle}>
        <div style={nameStyle}>{rule.name}</div>
        <Toggle
          checked={rule.status === "active"}
          onChange={onToggleStatus}
          ariaLabel={`Status for ${rule.name}`}
        />
      </div>

      <Field label="Applied to">
        <span style={appliedChipStyle}>
          <span
            style={{
              ...appliedDotStyle,
              background: getAppliedToDotColor(rule),
            }}
          />
          {getAppliedToLabel(rule)}
        </span>
      </Field>

      <Field label="If">
        {rule.conditions.map((cond, idx) => {
          const parts = describeCondition(cond);
          return (
            <div key={cond.id}>
              {idx > 0 && (
                <span style={condJoinStyle}>{rule.conditionJoin}</span>
              )}
              <div style={condLineStyle}>
                <span style={condKeyStyle}>{parts.keyLabel}</span>
                <span style={condOpStyle}>{parts.operatorLabel}</span>
                <span style={condValStyle}>{parts.valueLabel}</span>
              </div>
            </div>
          );
        })}
      </Field>

      <Field label="Then">
        {describeSettings(rule).length === 0 ? (
          <span style={{ fontSize: 11.5, color: PALETTE.ink4 }}>None</span>
        ) : (
          <div>
            {describeSettings(rule).map((label, idx) => (
              <span key={idx} style={settingsChipStyle}>
                {label}
              </span>
            ))}
          </div>
        )}
      </Field>

      <div style={footerStyle}>
        <span>{footerText}</span>
        <button
          ref={menuBtnRef}
          type="button"
          aria-label={`Actions for ${rule.name}`}
          onClick={handleMenuClick}
          style={menuBtnStyle}
        >
          ⋯
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={fieldStyle}>
      <div style={fieldLabelStyle}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}

function Toggle({ checked, onChange, ariaLabel }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      style={toggleStyle}
    >
      <span
        style={{
          position: "absolute",
          inset: 0,
          background: checked ? PALETTE.ink : PALETTE.line,
          borderRadius: 10,
          transition: "background 0.2s",
        }}
      >
        <span
          style={{
            position: "absolute",
            height: 16,
            width: 16,
            left: 2,
            top: 2,
            background: "#ffffff",
            borderRadius: "50%",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.18)",
            transition: "transform 0.2s",
            transform: checked ? "translateX(14px)" : "translateX(0)",
          }}
        />
      </span>
    </button>
  );
}

const priorityLabelStyle: CSSProperties = {
  fontSize: 10.5,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: PALETTE.ink4,
  fontWeight: 700,
};

const headStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 8,
};

const nameStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: PALETTE.ink,
  lineHeight: 1.3,
  flex: 1,
};

const fieldStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 3,
};

const fieldLabelStyle: CSSProperties = {
  fontSize: 10,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  fontWeight: 700,
  color: PALETTE.ink4,
};

const appliedChipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "3px 9px",
  background: PALETTE.bgFade,
  borderRadius: 4,
  fontSize: 11.5,
  fontWeight: 500,
  color: PALETTE.ink2,
};

const appliedDotStyle: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
};

const condLineStyle: CSSProperties = {
  fontSize: 12,
  color: PALETTE.ink2,
  lineHeight: 1.6,
};

const condKeyStyle: CSSProperties = {
  color: PALETTE.ink4,
  fontWeight: 500,
};

const condOpStyle: CSSProperties = {
  color: PALETTE.ink4,
  fontWeight: 500,
  margin: "0 3px",
};

const condValStyle: CSSProperties = {
  color: PALETTE.ink,
  fontWeight: 600,
  fontFamily: "ui-monospace, Menlo, monospace",
  fontSize: 11.5,
  background: PALETTE.bgFade,
  padding: "1px 5px",
  borderRadius: 3,
};

const condJoinStyle: CSSProperties = {
  color: PALETTE.ink4,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.08em",
  margin: "4px 0",
  display: "block",
};

const settingsChipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "3px 8px",
  fontSize: 11.5,
  fontWeight: 500,
  color: PALETTE.ink2,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 5,
  background: "#ffffff",
  margin: "2px 3px 2px 0",
  whiteSpace: "nowrap",
};

const footerStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingTop: 8,
  borderTop: `1px solid ${PALETTE.lineSoft}`,
  fontSize: 11.5,
  color: PALETTE.ink4,
};

const toggleStyle: CSSProperties = {
  position: "relative",
  display: "inline-block",
  width: 34,
  height: 20,
  background: "transparent",
  border: 0,
  padding: 0,
  cursor: "pointer",
  flexShrink: 0,
};

const menuBtnStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  color: PALETTE.ink3,
  fontSize: 18,
  padding: "4px 8px",
  cursor: "pointer",
  borderRadius: 6,
  lineHeight: 1,
};